import { Component, Input } from '@angular/core';
import { Department } from '@app/models/company/department.model';
import { Office } from '@app/models/company/office.model';
import { Employee } from '@app/models/employee/employee.model';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import { BaseForm } from '@app/modules/forms/base.form';
import { ReportFilter } from '@app/modules/reporting/interfaces/report-filter.interface';
import { TimeOffDateFormatDashes } from '@app/modules/time-off-v3/meta/time-off-meta';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import uniqBy from 'lodash-es/uniqBy';
import moment, { Moment } from 'moment';
import { FeatureFlag } from '@app/enums';

@Component({
    selector: 'app-time-off-report-filter-form',
    templateUrl: './time-off-report-filter.form.html',
    styleUrls: ['./time-off-report-filter.form.scss'],
})
export class TimeOffReportFilterForm extends BaseForm {
    @Input() timeOffTypes: TimeOffType[] = [];
    @Input() selectedTimeOffTypeId: number;
    @Input() selectedTimeOffTypesId: number[];
    @Input() withMultiTimeOffTypes = false;

    startDate: Moment = moment().startOf('year');
    endDate: Moment = moment().endOf('year');
    status: string;
    departments: Department[] = [];
    offices: Office[] = [];
    managers: Employee[] = [];

    hasImprovedTimeOffReports = false;
    selectedDepartmentId: number;
    selectedOfficeId: number;
    selectedManagerId: number;
    includePending: boolean = false;

    constructor(
        private auth: AuthService,
        private readonly featureService: FeatureService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        await this.setFeatureFlag();
        this.loadDepartments();
        this.loadOffices();
        this.loadManagers();
    }

    get filters(): ReportFilter[] {
        return [
            {
                name: 'startAt',
                value: this.startDate.format(TimeOffDateFormatDashes),
            },
            {
                name: 'endAt',
                value: this.endDate.format(TimeOffDateFormatDashes),
            },
            {
                name: 'timeOffTypeId',
                value: this.selectedTimeOffTypeId,
            },
            {
                name: 'timeOffTypesId',
                value: this.selectedTimeOffTypesId,
            },
            {
                name: 'status',
                value: this.status,
            },
            {
                name: 'department_id',
                value: this.selectedDepartmentId,
            },
            {
                name: 'office_id',
                value: this.selectedOfficeId,
            },
            {
                name: 'manager_id',
                value: this.selectedManagerId,
            },
            {
                name: 'include_pending',
                value: this.includePending?.toString(),
            },
        ];
    }

    loadDepartments(): void {
        Department.param('company', this.auth.company.id)
            .orderBy('name', 'ASC')
            .all()
            .then(([departments]) => {
                this.departments = departments;
            });
    }

    loadOffices(): void {
        Office.param('company', this.auth.company.id)
            .all()
            .then(([offices]) => {
                this.offices = offices;
            });
    }

    loadManagers(): void {
        Employee.param('company', this.auth.company.id)
            .all()
            .then(([employees]) => {
                // Get the uniques ids for reportsTo which will give us all of the managers
                const managersIds: number[] = uniqBy<Employee>(employees, 'reportsTo')
                    .filter((employee: Employee) => !!employee.reportsTo)
                    .map((employee: Employee) => employee.reportsTo);

                // Get the managers
                this.managers = employees.filter((employee: Employee) => {
                    return managersIds.includes(employee.id);
                });
            });
    }

    private async setFeatureFlag(): Promise<void> {
        this.hasImprovedTimeOffReports = await this.featureService.has(FeatureFlag.improvedTimeOffReports);
    }
}
